import Joi from '@hapi/joi';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from '../../mixins/translate/translate';
import { IValidationComponentState, useOldValidation, ValidationHelpText, ValidationRenderMessage } from '../../mixins/validation/validation';
import { fetchGet, fetchPost } from '../../store/fetch';
import { Roles } from '../../utils/enums/Roles';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelHeader,
    EditPanelInput,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    EditPanelSwitch,
    Panel,
    PanelBody,
    Topbar,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeft,
    Tray,
} from '../elements';
import { CountrySelect } from './CountrySelect';

interface ICompanyAddEditFormState extends IValidationComponentState {
    message?: string;
    submitting: boolean;
    failed: boolean;
    errorCode?: string;
    name?: string;
    storageMb?: string;
    status?: string;
    role?: Roles;
    state?: string;
    city?: string;
    zip?: string;
    street?: string;
    country?: string;
    isClient?: boolean;
    useBatches: boolean;
    useSurveyComments: boolean;
    useNen2767Advanced: boolean;
    authenticationToken?: string;
    latitude?: number;
    longitude?: number;
    isPdokMapLayerAvailable: boolean;
    areLabelsVisibleInOverviews: boolean;
    isDroneNoFlyMapNlAvailable: boolean;
    shouldClusterMarkers: boolean;
    restrictedHostName: string;
    useObjectRelations: boolean;
    shouldEnforceMfa: boolean;
    useEquipmentManagement: boolean;
    useProductTitle: boolean;
    daysVisibilityFutureSurveys: number;
    useAiChat: boolean;
    useAiDocumentUpload: boolean;
    useAiDefectDetection: boolean;
    aiDailyTokenLimit: number;
    aiMonthlyTokenLimit: number;
}

function hasValidationErrors(messages: unknown): boolean {
    return Array.isArray(messages) && messages.length > 0;
}

function CompanyAddEdit() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const userRole = useSelector((state: { authentication: { role: Roles } }) => state.authentication.role);

    const { validate, formState, handleChange, setFormState, handleValidation, getValidationMessages } = useOldValidation<ICompanyAddEditFormState>({
        initialState: {
            status: 'enabled',
            country: 'NL',
            name: '',
            state: '',
            city: '',
            zip: '',
            street: '',
            storageMb: '',
            useBatches: false,
            useSurveyComments: false,
            useNen2767Advanced: false,
            isPdokMapLayerAvailable: true,
            areLabelsVisibleInOverviews: false,
            isDroneNoFlyMapNlAvailable: false,
            shouldClusterMarkers: false,
            submitting: false,
            failed: false,
            errors: {},
            restrictedHostName: '',
            useObjectRelations: false,
            shouldEnforceMfa: false,
            useEquipmentManagement: false,
            useProductTitle: false,
            daysVisibilityFutureSurveys: 30,
            useAiChat: false,
            useAiDocumentUpload: false,
            useAiDefectDetection: false,
            aiDailyTokenLimit: 1000000,
            aiMonthlyTokenLimit: 20000000,
        },
        getValidatorTypes() {
            return {
                name: Joi.string().required().label(translate('company.companyName')),
                storageMb: Joi.number().allow('').integer().label(translate('company.storageMb')),
                role: Joi.string().required().label(translate('company.companyRole')),
                latitude: Joi.number().label(translate('object.latitude')).allow(null, ''),
                longitude: Joi.number().label(translate('object.longitude')).allow(null, ''),
            };
        },
        getValidatorData(innerFormState: ICompanyAddEditFormState) {
            return {
                name: innerFormState.name,
                storageMb: innerFormState.storageMb,
                status: innerFormState.status,
                role: innerFormState.role,
                state: innerFormState.state,
                city: innerFormState.city,
                zip: innerFormState.zip,
                street: innerFormState.street,
                country: innerFormState.country,
                isClient: innerFormState.isClient,
                useBatches: innerFormState.useBatches,
                useSurveyComments: innerFormState.useSurveyComments,
                useNen2767Advanced: innerFormState.useNen2767Advanced,
                authenticationToken: innerFormState.authenticationToken,
                latitude: innerFormState.latitude,
                longitude: innerFormState.longitude,
                isPdokMapLayerAvailable: innerFormState.isPdokMapLayerAvailable,
                areLabelsVisibleInOverviews: innerFormState.areLabelsVisibleInOverviews,
                isDroneNoFlyMapNlAvailable: innerFormState.isDroneNoFlyMapNlAvailable,
                shouldClusterMarkers: innerFormState.shouldClusterMarkers,
                restrictedHostName: innerFormState.restrictedHostName,
                useObjectRelations: innerFormState.useObjectRelations,
                shouldEnforceMfa: innerFormState.shouldEnforceMfa,
                useEquipmentManagement: innerFormState.useEquipmentManagement,
                useProductTitle: innerFormState.useProductTitle,
                daysVisibilityFutureSurveys: innerFormState.daysVisibilityFutureSurveys,
                useAiChat: innerFormState.useAiChat,
                useAiDocumentUpload: innerFormState.useAiDocumentUpload,
                useAiDefectDetection: innerFormState.useAiDefectDetection,
                aiDailyTokenLimit: innerFormState.aiDailyTokenLimit,
                aiMonthlyTokenLimit: innerFormState.aiMonthlyTokenLimit,
            };
        },
    });

    React.useEffect(() => {
        if (id) {
            const load = async () => {
                const url = `/api/companies/${id}`;
                const result = await fetchGet<ICompanyAddEditFormState & { storageLimit: number }>(url);
                if (result.json) {
                    const json = { ...result.json, storageMb: String(Math.ceil(result.json.storageLimit / 1024 / 1024)) };
                    setFormState(json);
                }
            };
            void load();
        }
    }, [id, setFormState]);

    function saveClicked(shouldGoToAddUser: boolean) {
        validate(async (validationError: Record<string, string> | undefined) => {
            if (validationError) {
                console.warn(validationError);
            } else {
                setFormState({ ...formState, submitting: true });
                const data: Record<string, unknown> = { ...formState };
                if (formState.storageMb) {
                    data.storageLimit = parseInt(formState.storageMb, 10) * 1024 * 1024;
                }

                let url = '/api/companies';
                if (id) {
                    url += `/${id}`;
                }

                const result = await fetchPost(url, {}, data);
                if (shouldGoToAddUser) {
                    const json = result.json as { id: string } | undefined;
                    navigate(`/companies/addUser?companyId=${json?.id ?? ''}`);
                } else {
                    navigate(-1);
                }
            }
        });
    }

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={formState.name} icon="fa-building" />
                    <TopbarCrumb>
                        <Link to={`/companies/${id}`}>{translate('company.details')}</Link>
                    </TopbarCrumb>
                    <TopbarCrumb>
                        <Link to="/companies">{translate('common.companies')}</Link>
                    </TopbarCrumb>
                </TopbarLeft>
            </Topbar>
            <Content>
                <Tray>
                    <ValidationRenderMessage {...formState} />
                    <Panel small>
                        <form method="post" action="/" id="admin-form">
                            <PanelBody>
                                {/* company name */}
                                <EditPanelSection>
                                    <EditPanelInput
                                        name="name"
                                        placeholder={translate('company.companyName')}
                                        value={formState.name}
                                        icon="fa fa-briefcase"
                                        handleChange={handleChange}
                                        notValid={hasValidationErrors(getValidationMessages('name'))}
                                        handleBlur={() => handleValidation()}
                                    />
                                </EditPanelSection>
                                <EditPanelSection>
                                    <EditPanelInput
                                        name="street"
                                        placeholder={translate('company.streetAndNumber')}
                                        value={formState.street}
                                        icon="fa fa-road"
                                        handleChange={handleChange}
                                    />
                                </EditPanelSection>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={6}>
                                        <EditPanelInput
                                            name="zip"
                                            placeholder={translate('company.zipCode')}
                                            value={formState.zip}
                                            icon="fa fa-square-o"
                                            handleChange={handleChange}
                                        />
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={6}>
                                        <EditPanelInput
                                            name="city"
                                            placeholder={translate('company.city')}
                                            value={formState.city}
                                            icon="fa fa-square-o"
                                            handleChange={handleChange}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={6}>
                                        <EditPanelInput
                                            name="state"
                                            placeholder={translate('company.state')}
                                            value={formState.state}
                                            icon="fa fa-square-o"
                                            handleChange={handleChange}
                                        />
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={6}>
                                        <CountrySelect
                                            initialValue={formState.country}
                                            onChange={async (country) => handleChange(country, 'country')}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                {/* role */}
                                <EditPanelHeader text={translate('common.settings')} />
                                <EditPanelRow>
                                    <EditPanelCell rowCount={6}>
                                        <EditPanelSelect
                                            name="role"
                                            value={formState.role}
                                            topLabel={translate('company.companyCompanyRole')}
                                            handleChange={handleChange}
                                            handleBlur={() => handleValidation()}
                                            disabled={formState.submitting}
                                            notValid={hasValidationErrors(getValidationMessages('role'))}
                                            placeholder={translate('common.selectItem', translate('company.companyRole'))}
                                            options={[
                                                ...[],
                                                ...(userRole === Roles.Administrator
                                                    ? [{ value: Roles.Administrator, text: translate(`common.${Roles.Administrator}`) }]
                                                    : []),
                                                ...(userRole !== Roles.Owner
                                                    ? [{ value: Roles.Owner, text: translate(`common.${Roles.Owner}`) }]
                                                    : []),
                                                ...(userRole !== Roles.Surveyor
                                                    ? [{ value: Roles.Surveyor, text: translate(`common.${Roles.Surveyor}`) }]
                                                    : []),
                                                ...(userRole !== Roles.Operator || formState.isClient
                                                    ? [{ value: Roles.Operator, text: translate(`common.${Roles.Operator}`) }]
                                                    : []),
                                            ]}
                                        />
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={6}>
                                        <EditPanelSelect
                                            name="status"
                                            value={formState.status}
                                            handleChange={handleChange}
                                            disabled={formState.submitting}
                                            topLabel={translate('common.status')}
                                            placeholder={translate('common.selectItem', translate('common.status'))}
                                            options={[
                                                { value: 'enabled', text: translate('common.enabled') },
                                                { value: 'disabled', text: translate('common.disabled') },
                                            ]}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                <EditPanelSection>
                                    <EditPanelSwitch
                                        name="shouldEnforceMfa"
                                        placeholder={translate('company.shouldEnforceMfa')}
                                        value={formState.shouldEnforceMfa}
                                        handleChange={handleChange}
                                        toolTip={translate('company.shouldEnforceMfaExplanation')}
                                    />
                                </EditPanelSection>
                                {userRole === Roles.Administrator && (
                                    <>
                                        <EditPanelSection>
                                            <EditPanelSwitch
                                                name="isClient"
                                                placeholder={translate('company.isClient')}
                                                value={formState.isClient}
                                                handleChange={handleChange}
                                                toolTip={translate('company.isClientExplanation')}
                                            />
                                        </EditPanelSection>

                                        {(formState.isClient || formState.role === Roles.Administrator) && (
                                            <>
                                                <EditPanelHeader text={translate('company.clientSettings')} />
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <EditPanelInput
                                                            name="authenticationToken"
                                                            topLabel={translate('company.authenticationToken')}
                                                            topLabelTooltip={translate('company.authenticationTokenExplanation')}
                                                            placeholder={translate('company.authenticationToken')}
                                                            value={formState.authenticationToken}
                                                            icon="fa fa-inbox"
                                                            handleChange={handleChange}
                                                        />
                                                    </EditPanelCell>
                                                    <EditPanelCell rowCount={6}>
                                                        <EditPanelInput
                                                            name="restrictedHostName"
                                                            topLabel={translate('company.restrictedHostName')}
                                                            topLabelTooltip={translate('company.restrictedHostNameExplanation')}
                                                            placeholder={translate('company.restrictedHostName')}
                                                            value={formState.restrictedHostName}
                                                            icon="fa fa-inbox"
                                                            handleChange={handleChange}
                                                        />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                            </>
                                        )}

                                        {(formState.isClient || formState.role === Roles.Administrator) && !isCustomerVersion('ronik') && (
                                            <>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="useBatches"
                                                        placeholder={translate('company.useBatches')}
                                                        value={formState.useBatches}
                                                        handleChange={handleChange}
                                                        toolTip={translate('company.useBatchesExplanation')}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="useSurveyComments"
                                                        placeholder={translate('company.useSurveyComments')}
                                                        value={formState.useSurveyComments}
                                                        handleChange={handleChange}
                                                        toolTip={translate('company.useSurveyCommentsExplanation')}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="useProductTitle"
                                                        placeholder={translate('company.useProductTitle')}
                                                        value={formState.useProductTitle}
                                                        handleChange={handleChange}
                                                        toolTip={translate('company.useProductTitleExplanation')}
                                                    />
                                                </EditPanelSection>

                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="areLabelsVisibleInOverviews"
                                                        placeholder={translate('company.areLabelsVisibleInOverviews')}
                                                        value={formState.areLabelsVisibleInOverviews}
                                                        handleChange={handleChange}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="useNen2767Advanced"
                                                        placeholder={translate('company.useNen2767Advanced')}
                                                        value={formState.useNen2767Advanced}
                                                        handleChange={handleChange}
                                                        toolTip={translate('company.useNen2767AdvancedExplanation')}
                                                    />
                                                </EditPanelSection>
                                            </>
                                        )}
                                        {(formState.isClient || formState.role === Roles.Administrator) && (
                                            <>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={5}>
                                                        <EditPanelInput
                                                            name="daysVisibilityFutureSurveys"
                                                            placeholder={translate('company.daysVisibilityFutureSurveys')}
                                                            value={formState.daysVisibilityFutureSurveys}
                                                            icon="fa fa-circle-o"
                                                            handleChange={handleChange}
                                                            topLabel={translate('company.daysVisibilityFutureSurveys')}
                                                            topLabelTooltip={translate('company.daysVisibilityFutureSurveysExplanation')}
                                                            restrictToNumbers={true}
                                                        />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="useEquipmentManagement"
                                                        placeholder={translate('company.useEquipmentManagement')}
                                                        value={formState.useEquipmentManagement}
                                                        handleChange={handleChange}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="useObjectRelations"
                                                        placeholder={translate('objectRelation.useObjectRelations')}
                                                        value={formState.useObjectRelations}
                                                        handleChange={handleChange}
                                                        toolTip={translate('objectRelation.useObjectRelationsExplanation')}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelHeader text="AI" />
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="useAiChat"
                                                        placeholder={translate('company.useAiChat')}
                                                        value={formState.useAiChat}
                                                        handleChange={handleChange}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="useAiDocumentUpload"
                                                        placeholder={translate('company.useAiDocumentUpload')}
                                                        value={formState.useAiDocumentUpload}
                                                        handleChange={handleChange}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="useAiDefectDetection"
                                                        placeholder={translate('company.useAiDefectDetection')}
                                                        value={formState.useAiDefectDetection}
                                                        handleChange={handleChange}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <EditPanelInput
                                                            name="aiDailyTokenLimit"
                                                            placeholder={translate('company.aiDailyTokenLimit')}
                                                            value={formState.aiDailyTokenLimit}
                                                            icon="fa fa-circle-o"
                                                            handleChange={handleChange}
                                                            topLabel={translate('company.aiDailyTokenLimit')}
                                                            restrictToNumbers={true}
                                                        />
                                                    </EditPanelCell>
                                                    <EditPanelCell rowCount={6}>
                                                        <EditPanelInput
                                                            name="aiMonthlyTokenLimit"
                                                            placeholder={translate('company.aiMonthlyTokenLimit')}
                                                            value={formState.aiMonthlyTokenLimit}
                                                            icon="fa fa-circle-o"
                                                            handleChange={handleChange}
                                                            topLabel={translate('company.aiMonthlyTokenLimit')}
                                                            restrictToNumbers={true}
                                                        />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                            </>
                                        )}
                                        {formState.isClient && (
                                            <>
                                                <EditPanelHeader text={translate('company.mapSettings')} />
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="shouldClusterMarkers"
                                                        placeholder={translate('company.shouldClusterMarkers')}
                                                        value={formState.shouldClusterMarkers}
                                                        handleChange={handleChange}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="isPdokMapLayerAvailable"
                                                        placeholder={translate('company.isPdokMapLayerAvailable')}
                                                        value={formState.isPdokMapLayerAvailable}
                                                        handleChange={handleChange}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelSection>
                                                    <EditPanelSwitch
                                                        name="isDroneNoFlyMapNlAvailable"
                                                        placeholder={translate('company.isDroneNoFlyMapNlAvailable')}
                                                        value={formState.isDroneNoFlyMapNlAvailable}
                                                        handleChange={handleChange}
                                                    />
                                                </EditPanelSection>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <EditPanelInput
                                                            name="latitude"
                                                            topLabel="Latitude"
                                                            topLabelTooltip={translate('company.mapSettingsExplanation')}
                                                            placeholder={translate('object.latitude')}
                                                            value={formState.latitude ? formState.latitude.toString() : ''}
                                                            icon="fa fa-circle-o"
                                                            handleChange={handleChange}
                                                            notValid={hasValidationErrors(getValidationMessages('latitude'))}
                                                        />
                                                        <ValidationHelpText message={getValidationMessages('latitude')} />
                                                    </EditPanelCell>
                                                    <EditPanelCell rowCount={6}>
                                                        <EditPanelInput
                                                            name="longitude"
                                                            topLabel="Longitude"
                                                            topLabelTooltip={translate('company.mapSettingsExplanation')}
                                                            placeholder={translate('object.longitude')}
                                                            value={formState.longitude ? formState.longitude.toString() : ''}
                                                            icon="fa fa-circle-o"
                                                            handleChange={handleChange}
                                                            notValid={hasValidationErrors(getValidationMessages('longitude'))}
                                                        />
                                                        <ValidationHelpText message={getValidationMessages('longitude')} />
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                            </>
                                        )}
                                    </>
                                )}
                            </PanelBody>
                            <EditPanelButtons footer={true}>
                                <EditPanelButton
                                    onClick={() => navigate('/companies')}
                                    text={translate('common.cancel')}
                                    small={false}
                                    disabled={formState.submitting}
                                />
                                <EditPanelButton
                                    onClick={() => saveClicked(true)}
                                    text={translate('company.saveCompanyAndAddUser')}
                                    small={false}
                                    primary={false}
                                    disabled={formState.submitting}
                                />
                                <EditPanelButton
                                    onClick={() => saveClicked(false)}
                                    text={translate('common.save')}
                                    small={false}
                                    primary={true}
                                    disabled={formState.submitting}
                                />
                            </EditPanelButtons>
                        </form>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}

// eslint-disable-next-line import/no-default-export
export default CompanyAddEdit;
